import React, { useState } from 'react';
import useInView from './useInView';

const services = [
  {
    num: '01', title: 'Civil & Building Construction',
    desc: 'Full-spectrum civil engineering and building construction with a proven track record in major project implementation across Nigeria.',
    items: ['Road & highway construction', 'Bridge construction & renovation', 'Stadium & public infrastructure', 'Residential & commercial buildings', 'Industrial facilities'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="26" height="26">
        <path d="M3 21h18M3 9l9-7 9 7M5 21V9m14 12V9M9 21v-6h6v6" />
      </svg>
    ),
  },
  {
    num: '02', title: 'Electrical & Mechanical Engineering',
    desc: 'Comprehensive electrical installations and mechanical engineering solutions for residential, commercial, and industrial projects.',
    items: ['Power installation & wiring', 'HVAC & mechanical systems', 'Generator installation', 'Industrial mechanical works', 'Plumbing & pipework'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="26" height="26">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
  },
  {
    num: '03', title: 'Borehole Drilling & Water Engineering',
    desc: 'Expert groundwater solutions from geophysical survey to full water supply systems — delivering clean water across Nigeria\'s communities.',
    items: ['Geophysical survey & siting', 'Borehole drilling & casing', 'Submersible & solar pumps', 'Water treatment plants', 'Overhead tank installation'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="26" height="26">
        <circle cx="12" cy="12" r="3" /><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
      </svg>
    ),
  },
  {
    num: '04', title: 'General Contracting',
    desc: 'End-to-end project management and contracting for public sector, NGOs, and private clients nationwide.',
    items: ['Government contracts', 'NGO infrastructure projects', 'Procurement & supply', 'Full project management', 'Maintenance services'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="26" height="26">
        <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /><line x1="12" y1="12" x2="12" y2="16" /><line x1="10" y1="14" x2="14" y2="14" />
      </svg>
    ),
  },
];

export default function Services() {
  const [hovered, setHovered] = useState(null);
  const [ref, inView] = useInView();

  return (
    <section id="services" style={{ padding: 'clamp(3rem, 8vw, 8rem) 0', background: '#0a1f5c', position: 'relative', overflow: 'hidden' }}>
      {/* BG text watermark */}
      <div style={{
        position: 'absolute', right: '-2rem', top: '2rem',
        fontFamily: "'Bebas Neue', sans-serif", fontSize: '14rem', fontWeight: 400,
        color: 'rgba(26,79,156,0.07)', lineHeight: 1,
        pointerEvents: 'none', userSelect: 'none', whiteSpace: 'nowrap',
      }}>SERVICES</div>

      <div className="section-wrap">
        {/* Header */}
        <div ref={ref} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          marginBottom: '3.5rem', flexWrap: 'wrap', gap: '1.5rem',
          opacity: inView ? 1 : 0, transform: inView ? 'none' : 'translateY(24px)',
          transition: 'all 0.7s ease',
        }}>
          <div>
            <div className="section-label light">What We Do</div>
            <h2 className="section-title light">Our Core <span>Services</span></h2>
          </div>
          <p className="section-body light" style={{ maxWidth: 360 }}>Four specialist divisions delivering integrated engineering solutions across Nigeria.</p>
        </div>

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2px' }}>
          {services.map((s, i) => (
            <div key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{
                background: hovered === i ? '#1a4f9c' : '#ffffff',
                padding: '2.5rem 2rem',
                borderBottom: `3px solid ${hovered === i ? '#7aaef0' : 'transparent'}`,
                transition: 'all 0.3s ease',
                cursor: 'default',
                position: 'relative', overflow: 'hidden',
                transform: hovered === i ? 'translateY(-4px)' : 'none',
                opacity: inView ? 1 : 0,
                transitionDelay: `${i * 0.08}s`,
              }}>
              {/* Ghost number */}
              <div style={{
                position: 'absolute', top: '1rem', right: '1rem',
                fontFamily: "'Bebas Neue', sans-serif", fontSize: '4rem',
                color: 'rgba(255,255,255,0.04)', lineHeight: 1, pointerEvents: 'none',
              }}>{s.num}</div>

              {/* Icon */}
              <div style={{
                width: 52, height: 52,
                background: hovered === i ? 'rgba(255,255,255,0.2)' : 'rgba(26,79,156,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '1.5rem', color: '#0d2144',
                transition: 'background 0.3s',
                clipPath: 'polygon(0 0, 85% 0, 100% 15%, 100% 100%, 15% 100%, 0 85%)',
              }}>
                {s.icon}
              </div>

              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '1.05rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#0d2144', marginBottom: '0.8rem' }}>{s.title}</div>
              <p style={{ fontSize: '0.83rem', color: hovered === i ? 'white' : '#0d2144', lineHeight: 1.65, fontWeight: 300, marginBottom: '1.2rem' }}>{s.desc}</p>

              <ul style={{ listStyle: 'none' }}>
                {s.items.map((item, j) => (
                  <li key={j} style={{
                    fontSize: '0.75rem',
                    color: hovered === i ? 'white' : '#1a4f9c',
                    padding: '0.28rem 0',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    transition: 'color 0.3s',
                  }}>
                    <span style={{ color: hovered === i ? '#7aaef0' : 'var(--blue)', fontSize: '0.65rem' }}>→</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* CTA strip */}
          <div style={{
            gridColumn: '1 / -1',
            background: 'var(--blue)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '2rem 3rem', flexWrap: 'wrap', gap: '1rem',
          }}>
            <p style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: '1.2rem', color: 'white', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ready to start your next project with Rigomich?
            </p>
            <a href="#contact" className="btn-white">Request a Quote →</a>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          #services .section-wrap > div:last-child { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 600px) {
          #services .section-wrap > div:last-child { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
